import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastController } from '@ionic/angular/standalone';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { AppInfoService } from '../../services/app-info.service';
import { LoggerService } from '../../services/logger.service';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonSpinner,
  IonText,
  IonModal,
  IonIcon
} from '@ionic/angular/standalone';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StorageService } from '../../services/storage.service';
import { normalizeEmail } from 'shared_models';
import { addIcons } from 'ionicons';
import { eyeOutline, eyeOffOutline, close } from 'ionicons/icons';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonItem,
    IonLabel,
    IonInput,
    IonButton,
    IonSpinner,
    IonText,
    IonModal,
    IonIcon
  ]
})
export class LoginPage implements OnInit, OnDestroy {
  username: string = '';
  password: string = '';
  isLoading: boolean = false;
  apiUrl: string = '';
  isProd: boolean = false;
  errorMessage: string = '';
  private loginTimeout: any;
  environment = environment; // Expose environment to template

  // Password visibility and display properties
  showPassword: boolean = false;

  // Forgot password password visibility and display properties
  showForgotPassword: boolean = false;

  // Forgot password modal state
  showForgotPasswordModal: boolean = false;
  forgotStep: 'request' | 'confirm' = 'request';
  forgotUsername: string = '';
  forgotCode: string = '';
  forgotNewPassword: string = '';
  forgotLoading: boolean = false;
  forgotError: string = '';

  appVersion: string = '';

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastController: ToastController,
    private storageService: StorageService,
    private appInfoService: AppInfoService,
    private logger: LoggerService
  ) {
    this.apiUrl = environment.apiUrl;
    this.isProd = environment.environmentName === 'production';
    addIcons({ eyeOutline, eyeOffOutline, close });
  }

  async ngOnInit() {
    // Allow portrait and landscape on the login screen
    try {
      await ScreenOrientation.unlock();
    } catch (error) {
      this.logger.error('Error unlocking screen orientation:', error);
    }

    // Fetch app version
    try {
      const appInfo = await this.appInfoService.getAppInfo();
      this.appVersion = appInfo.version;
    } catch (error) {
      this.logger.error('Error fetching app version:', error);
    }

    // Reset any lingering states
    this.isLoading = false;
    this.errorMessage = '';
    this.password = '';
    
    try {
      // Load the last used username
      const lastUsername = await this.storageService.getLastUsername();
      if (lastUsername) {
        this.username = lastUsername;
      }
    } catch (error) {
      this.logger.error('Error loading last username:', error);
    }
  }

  async ngOnDestroy() {
    // Unlock screen orientation when leaving the login page
    try {
      await ScreenOrientation.unlock();
    } catch (error) {
      this.logger.error('Error unlocking screen orientation:', error);
    }

    if (this.loginTimeout) {
      clearTimeout(this.loginTimeout);
    }
    // Ensure loading state is reset
    this.isLoading = false;
  }

  private async handleLoginError(error: any) {
    // Reset UI state immediately
    this.isLoading = false;
    this.password = '';
    
    // Handle specific error cases
    switch (error.code) {
      case 'UserNotConfirmedException':
        this.errorMessage = 'Please verify your email address.';
        break;
      case 'NotAuthorizedException':
        this.errorMessage = 'Incorrect username or password.';
        break;
      case 'UserNotFoundException':
        this.errorMessage = 'User not found.';
        break;
      case 'InvalidParameterException':
        this.errorMessage = 'Invalid username or password format.';
        break;
      case 'TooManyRequestsException':
        this.errorMessage = 'Too many login attempts. Please try again later.';
        break;
      default:
        if (error.message === 'NEW_PASSWORD_REQUIRED') {
          this.router.navigate(['/change-password']);
          return;
        }
        this.errorMessage = 'Login failed. Please try again.';
    }
    
    // Show error message
    await this.showToast(this.errorMessage);
  }

  async onLogin() {
    // Clear any existing timeout
    if (this.loginTimeout) {
      clearTimeout(this.loginTimeout);
    }

    // Reset states
    this.errorMessage = '';
    
    if (!this.username || !this.password) {
      this.errorMessage = 'Please enter both username and password';
      await this.showToast(this.errorMessage);
      return;
    }

    // Normalize email address before login
    const normalizedUsername = normalizeEmail(this.username);

    try {
      // Set loading state
      this.isLoading = true;

      // Set a timeout to reset loading state if login takes too long
      this.loginTimeout = setTimeout(() => {
        this.isLoading = false;
        this.errorMessage = 'Login request timed out. Please try again.';
        this.showToast(this.errorMessage);
      }, 30000); // 30 second timeout

      const success = await this.authService.login(normalizedUsername, this.password);
      
      // Clear timeout since login completed
      clearTimeout(this.loginTimeout);
      
      if (success) {
        await this.storageService.saveLastUsername(this.username);
        this.router.navigate(['/landing']);
      } else {
        // Reset UI immediately on failure
        this.isLoading = false;
        this.password = '';
        this.errorMessage = 'Login failed. Please try again.';
        await this.showToast(this.errorMessage);
      }
    } catch (error: any) {
      // Clear timeout since login completed with error
      clearTimeout(this.loginTimeout);
      
      // Handle error and reset UI immediately
      await this.handleLoginError(error);
    } finally {
      // Ensure loading state and timeout are cleared
      this.isLoading = false;
      if (this.loginTimeout) {
        clearTimeout(this.loginTimeout);
      }
    }
  }

  openForgotPasswordModal() {
    this.showForgotPasswordModal = true;
    this.forgotStep = 'request';
    this.forgotUsername = '';
    this.forgotCode = '';
    this.forgotNewPassword = '';
    this.forgotError = '';
    this.forgotLoading = false;
    
    // Reset password visibility states
    this.showForgotPassword = false;
  }

  closeForgotPasswordModal() {
    this.showForgotPasswordModal = false;
  }

  async onForgotPasswordRequest() {
    if (!this.forgotUsername) {
      this.forgotError = 'Please enter your username or email.';
      return;
    }

    // Normalize email address before sending forgot password request
    const normalizedEmail = normalizeEmail(this.forgotUsername);

    try {
      this.forgotLoading = true;
      this.forgotError = '';
      await this.authService.forgotPassword(normalizedEmail);
      this.forgotStep = 'confirm';
    } catch (err: any) {
      this.forgotError = err?.message || 'Failed to send reset code.';
    } finally {
      this.forgotLoading = false;
    }
  }

  async onForgotPasswordConfirm() {
    if (!this.forgotCode || !this.forgotNewPassword) {
      this.forgotError = 'Please enter the code and new password.';
      return;
    }

    // Normalize email address before confirming forgot password
    const normalizedEmail = normalizeEmail(this.forgotUsername);

    try {
      this.forgotLoading = true;
      this.forgotError = '';
      await this.authService.confirmForgotPassword(normalizedEmail, this.forgotCode, this.forgotNewPassword);
      this.closeForgotPasswordModal();
      await this.showToast('Password reset successful. You can now log in.', 'success');
    } catch (err: any) {
      this.forgotError = err?.message || 'Failed to reset password.';
    } finally {
      this.forgotLoading = false;
    }
  }

  private async showToast(message: string, color: string = 'danger') {
    const toast = await this.toastController.create({
      message: message,
      duration: 3000,
      position: 'bottom',
      color,
      cssClass: 'login-error-toast'
    });
    await toast.present();
  }

  // Password visibility toggle
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  // Forgot password password visibility toggle
  toggleForgotPasswordVisibility() {
    this.showForgotPassword = !this.showForgotPassword;
  }
}
